import store from "./store";
import debugTools from "./debug";
import checkIfValidDateUsingNaN from "../util/checkIfValidDateUsingNaN";

/**
 * Parses and returns information for the experiments section of a target.
 * This function is only called once we know the user falls under the target
 * @param {Object} target - The target object with an experiments key
 * @returns false if the user does not fall under any experiments in this target
 * otherwise,
 * {
 *    experimentId: id of the selected experiment,
 *    bucketId: the id of the bucket in the experiment,
 *    config: the config for the corresponding bucket to use
 * }
 */

const evaluateExperiments = async (target) => {
    if (!target.experiments || !target.experiments.length) {
        return false;
    }

    await store.updateExperimentEndTimes(target);
    let selectedExperiment;
    const inExperiment = await store.getTargetPlacement(target.targetId);

    if (inExperiment) {
        selectedExperiment = target.experiments.find((e) => {
            return e.experimentId === inExperiment.experimentId;
        });

        if (selectedExperiment) {
            // The user was previously selected to be in this experiment/bucket. Terminate early.
            return useBucket(selectedExperiment, inExperiment.bucketId, target.config);
        } else {
            // The experiment was terminated early. Remove and continue.
            await store.removeTargetPlacement(target.targetId);
            selectedExperiment = null;
        }
    }

    const experimentPlacement = Math.random();
    let total = 0;
    let currentExperiment;

    for (let i = 0; i < target.experiments.length; i++) {
        currentExperiment = target.experiments[i];

        const previousDecision = await store.getExperimentPlacement(currentExperiment.experimentId);

        const enrollmentEndDate = new Date(currentExperiment?.enrollmentEndDate);

        if (!!previousDecision && previousDecision.bucketId !== null) {
            return useBucket(currentExperiment, previousDecision.bucketId, target.config);
        } else if (checkIfValidDateUsingNaN(enrollmentEndDate) && new Date() >= enrollmentEndDate) {
            debugTools.info("User did not fall into experiment because they are not currently in experiment and past the enrollmentEndDate");
            total += currentExperiment.population;
            continue;
        } else if (!!previousDecision && previousDecision.bucketId === null) {
            // We already decided the user didn't fall under this one
            total += currentExperiment.population;
            continue;
        }

        if (experimentPlacement >= total && experimentPlacement < total + currentExperiment.population) {
            selectedExperiment = currentExperiment;
            break;
        }

        // Remember that the user did not fall under this experiment
        await store.setExperimentPlacement(
            currentExperiment.experimentId,
            null,
            currentExperiment.endTime,
            currentExperiment.locked
        );
        total += currentExperiment.population;
    }

    if (!selectedExperiment) {
        return false;
    }

    const selectedBucket = useBucket(selectedExperiment, null, target.config);

    await store.setExperimentPlacement(
        selectedBucket.experimentId,
        selectedBucket.bucketId,
        selectedExperiment.endTime,
        selectedExperiment.locked
    );
    await store.setTargetPlacement(target.targetId, selectedBucket.experimentId, selectedBucket.bucketId);

    debugTools.info("Selected bucket:", selectedBucket);

    return selectedBucket;
};

export const useBucket = (selectedExperiment, bucketId, defaultConfig) => {
    let bucket;

    if (bucketId !== null) {
        bucket = selectedExperiment.buckets.find((b) => {
            return b.bucketId === bucketId;
        });
    } else {
        bucket = getRandomBucket(selectedExperiment);
    }

    return {
        experimentId: selectedExperiment.experimentId,
        bucketId: bucket.bucketId,
        endTime: selectedExperiment.endTime,
        config: {
            ...defaultConfig,
            ...bucket.config,
        },
        buckets: selectedExperiment.buckets,
    };
};

const getRandomBucket = (selected) => {
    const bucketNum = Math.floor(Math.random() * selected.buckets.length);

    return selected.buckets[bucketNum];
};

export default evaluateExperiments;
